import { useEffect, useState } from "react";
import { api, BRL, authFileUrl, authFileDownloadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ExpenseDialog, { RECUR_LABELS } from "@/components/ExpenseDialog";
import PaymentsDialog from "@/components/PaymentsDialog";
import { Plus, Pencil, Trash2, Paperclip, Download, Search, CheckCircle2, Circle, CircleDollarSign, Receipt, HandCoins, X } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [toDelete, setToDelete] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [monthFilter, setMonthFilter] = useState("");
  const [recFilter, setRecFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [categories, setCategories] = useState([]);

  const load = () => api.get("/expenses").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const toggle = async (id) => { await api.patch(`/expenses/${id}/toggle`); load(); };

  const remove = async () => {
    await api.delete(`/expenses/${toDelete.id}`);
    setToDelete(null);
    toast.success("Despesa excluída");
    load();
  };

  const isOverdue = (e) => e.status !== "paid" && new Date(e.date) < new Date(new Date().toDateString());

  const statusIcon = (e) => {
    if (e.status === "paid") return <CheckCircle2 className="h-5 w-5 text-primary" />;
    if (e.status === "partial") return <CircleDollarSign className="h-5 w-5 text-amber-600 dark:text-amber-500" />;
    return <Circle className="h-5 w-5 text-muted-foreground/50 group-hover:text-primary transition-colors" />;
  };

  const filtered = items.filter((e) => {
    const q = query.toLowerCase();
    const matchQ = e.name.toLowerCase().includes(q) || (e.description || "").toLowerCase().includes(q) || (e.contract || "").toLowerCase().includes(q);
    const matchF = filter === "all" || (filter === "pending" && e.status !== "paid") || (filter === "paid" && e.status === "paid");
    const matchMonth = !monthFilter || (e.date || "").slice(0, 7) === monthFilter;
    const matchRec = recFilter === "all" || e.recurrence === recFilter;
    const matchCat = catFilter === "all" || e.category_id === catFilter;
    return matchQ && matchF && matchMonth && matchRec && matchCat;
  });

  const totalFiltered = filtered.reduce((a, e) => a + (e.value || 0), 0);
  const hasExtraFilters = monthFilter || recFilter !== "all" || catFilter !== "all";
  const catColor = (id) => (categories.find((c) => c.id === id) || {}).color || "#94a3b8";

  return (
    <div className="fade-up space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-4xl font-extrabold tracking-tight">Despesas</h1>
          <p className="text-muted-foreground mt-1">Contas a pagar, pagamentos parciais e recorrências</p>
        </div>
        <Button data-testid="add-expense-button" className="font-semibold gap-2" onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="h-4 w-4" /> Nova despesa
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input data-testid="expense-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar despesa..." className="pl-9" />
        </div>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="expense-filter-tabs">
            <TabsTrigger value="all" data-testid="filter-all">Todas</TabsTrigger>
            <TabsTrigger value="pending" data-testid="filter-pending">A pagar</TabsTrigger>
            <TabsTrigger value="paid" data-testid="filter-paid">Pagas</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Mês (vencimento)</label>
          <Input type="month" data-testid="expense-month-filter" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="w-full sm:w-48" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Recorrência</label>
          <Select value={recFilter} onValueChange={setRecFilter}>
            <SelectTrigger data-testid="expense-recurrence-filter" className="w-full sm:w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {Object.entries(RECUR_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Categoria</label>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="expense-category-filter" className="w-full sm:w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />{c.name}</span></SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {hasExtraFilters && (
          <Button variant="ghost" className="gap-2 text-muted-foreground" data-testid="clear-filters-button" onClick={() => { setMonthFilter(""); setRecFilter("all"); setCatFilter("all"); }}>
            <X className="h-4 w-4" /> Limpar
          </Button>
        )}
        <div className="sm:ml-auto text-sm text-muted-foreground" data-testid="filtered-total">
          {filtered.length} despesa(s) · Total <span className="font-semibold text-foreground tnum">{BRL(totalFiltered)}</span>
        </div>
      </div>

      <Card className="border-border/60 shadow-none rounded-md overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-20 text-center" data-testid="expenses-empty">
            <Receipt className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" aria-hidden />
            <p className="text-muted-foreground">Nenhuma despesa por aqui.</p>
            <Button variant="link" className="text-primary" onClick={() => { setEditing(null); setDialogOpen(true); }}>Adicionar a primeira</Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Recorrência</TableHead>
                <TableHead className="text-right">Valor / Pago</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`} className="group">
                  <TableCell>
                    <button data-testid={`toggle-status-${e.id}`} onClick={() => toggle(e.id)} title="Alternar status">
                      {statusIcon(e)}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${e.status === "paid" ? "line-through text-muted-foreground" : ""}`}>{e.name}</span>
                      {e.status === "partial" && <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px]">Parcial</Badge>}
                      {e.attachment_path && (
                        <>
                          <a href={authFileUrl(e.attachment_path)} target="_blank" rel="noreferrer" title={`Ver ${e.attachment_name}`} data-testid={`attachment-link-${e.id}`}>
                            <Paperclip className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                          </a>
                          <a href={authFileDownloadUrl(e.attachment_path)} title={`Baixar ${e.attachment_name}`} data-testid={`attachment-download-${e.id}`}>
                            <Download className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                          </a>
                        </>
                      )}
                    </div>
                    {e.description && <p className="text-xs text-muted-foreground truncate max-w-[240px]">{e.description}</p>}
                    {e.category_name && (
                      <span className="inline-flex items-center gap-1.5 text-xs mt-0.5" data-testid={`expense-category-${e.id}`}>
                        <span className="h-2 w-2 rounded-full" style={{ background: catColor(e.category_id) }} />
                        <span className="text-muted-foreground">{e.category_name}</span>
                      </span>
                    )}
                    {(e.client_name || e.contract_title) && <p className="text-xs text-primary/80 truncate max-w-[240px]">{[e.client_name, e.contract_title].filter(Boolean).join(" · ")}</p>}
                  </TableCell>
                  <TableCell>
                    <span className={isOverdue(e) ? "text-destructive font-medium" : ""}>
                      {new Date(e.date).toLocaleDateString("pt-BR")}
                    </span>
                    {isOverdue(e) && <Badge variant="destructive" className="ml-2 text-[10px]">Vencida</Badge>}
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="font-normal">{RECUR_LABELS[e.recurrence]}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="font-semibold tnum">{BRL(e.value)}</div>
                    {e.paid > 0 && e.status !== "paid" && (
                      <div className="text-xs text-muted-foreground tnum" data-testid={`expense-remaining-${e.id}`}>
                        Pago {BRL(e.paid)} · Falta {BRL(e.remaining)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Pagamentos" data-testid={`payments-expense-${e.id}`} onClick={() => setPayFor(e)}>
                        <HandCoins className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-expense-${e.id}`} onClick={() => { setEditing(e); setDialogOpen(true); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-expense-${e.id}`} onClick={() => setToDelete(e)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <ExpenseDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} expense={editing} />
      <PaymentsDialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)} expense={payFor} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir despesa?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita. "{toDelete?.name}" será removida.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-button" className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
